import { useState, useEffect } from 'react'
import useAuthStore, { isDeveloper, ROLES } from '../store/authStore'
import useGhlStore from '../store/ghlStore'

function DevConsolePage() {
  const { user, updateUserRole } = useAuthStore()
  const { config, setConfig, webhookLog, isConfigured } = useGhlStore()

  const [ghlApiKey, setGhlApiKey]         = useState(config.apiKey || '')
  const [ghlLocationId, setGhlLocationId] = useState(config.locationId || '')
  const [ghlPipelineId, setGhlPipelineId] = useState(config.pipelineId || '')
  const [saveMsg, setSaveMsg]             = useState('')
  const [webhookEvents, setWebhookEvents] = useState([])
  const [healthStatus, setHealthStatus]   = useState(null)
  const [activeTab, setActiveTab]         = useState('api')

  // Guard: only developer or host can access
  if (!isDeveloper(user)) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#dc2626' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
        <h2>Access Denied</h2>
        <p>The Developer Console requires <strong>Developer</strong> or <strong>Host</strong> role.</p>
        <p style={{ color: '#64748b', fontSize: 13 }}>Current role: <strong>{user?.role || 'unknown'}</strong></p>
      </div>
    )
  }

  // Load webhook log from backend
  useEffect(() => {
    fetch('/api/ghl/webhook/log')
      .then(r => r.json())
      .then(d => setWebhookEvents(d.events || []))
      .catch(() => {})
  }, [])

  // Load system health
  useEffect(() => {
    fetch('/health')
      .then(r => r.json())
      .then(d => setHealthStatus(d))
      .catch(() => setHealthStatus({ status: 'unreachable' }))
  }, [])

  const handleSaveGhl = () => {
    setConfig({ apiKey: ghlApiKey, locationId: ghlLocationId, pipelineId: ghlPipelineId })
    setSaveMsg('Saved!')
    setTimeout(() => setSaveMsg(''), 2000)
  }

  const tabStyle = (id) => ({
    padding: '9px 18px', border: 'none', borderRadius: '8px 8px 0 0',
    background: activeTab === id ? '#1B3A6B' : '#e2e8f0',
    color: activeTab === id ? '#fff' : '#475569',
    fontWeight: 600, cursor: 'pointer', fontSize: 13,
  })

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'inline-block', background: '#fef3c7', color: '#92400e', padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, marginBottom: 8 }}>
          DEVELOPER CONSOLE
        </div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>System Configuration</h1>
        <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
          API keys, webhook logs, and system health — visible to Developer and Host roles only.
        </p>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 0, borderBottom: '2px solid #1B3A6B' }}>
        {[
          { id: 'api',     label: '🔑 API Keys' },
          { id: 'webhook', label: '📡 Webhook Log' },
          { id: 'health',  label: '💚 System Health' },
          { id: 'roles',   label: '👤 Role Manager' },
        ].map(t => (
          <button key={t.id} style={tabStyle(t.id)} onClick={() => setActiveTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 8px 8px 8px', padding: 28 }}>

        {/* ── API Keys Tab ── */}
        {activeTab === 'api' && (
          <div>
            <h2 style={{ margin: '0 0 16px', fontSize: 17, color: '#0f172a' }}>M2M API Configuration</h2>
            <div style={{ display: 'grid', gap: 14, maxWidth: 520 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4, color: '#475569' }}>M2M API Key</label>
                <input
                  type="password"
                  value={ghlApiKey}
                  onChange={e => setGhlApiKey(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 14, boxSizing: 'border-box' }}
                  placeholder="Bearer token from M2M"
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4, color: '#475569' }}>Location ID</label>
                <input
                  value={ghlLocationId}
                  onChange={e => setGhlLocationId(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 14, boxSizing: 'border-box' }}
                  placeholder="M2M Location/Sub-account ID"
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4, color: '#475569' }}>Pipeline ID</label>
                <input
                  value={ghlPipelineId}
                  onChange={e => setGhlPipelineId(e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 14, boxSizing: 'border-box' }}
                  placeholder="M2M Pipeline ID for deal tracking"
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  onClick={handleSaveGhl}
                  style={{ padding: '10px 24px', background: '#1B3A6B', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
                >
                  Save Configuration
                </button>
                {saveMsg && <span style={{ color: '#16a34a', fontWeight: 600, fontSize: 14 }}>{saveMsg}</span>}
              </div>
            </div>
            <div style={{ marginTop: 24, background: '#f8fafc', borderRadius: 8, padding: '14px 18px', fontSize: 13, color: '#475569' }}>
              <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#0f172a' }}>Webhook Endpoint</p>
              <code style={{ background: '#e2e8f0', padding: '4px 8px', borderRadius: 5, fontSize: 12 }}>
                POST {window.location.origin}/api/ghl/webhook
              </code>
              <p style={{ margin: '8px 0 0' }}>Configure this URL in your M2M sub-account Webhook settings to receive real-time events.</p>
              <p style={{ margin: '6px 0 0' }}>Supported events: <code>contact.created</code>, <code>opportunity.stageChange</code></p>
            </div>
            <div style={{ marginTop: 16, background: isConfigured ? '#dcfce7' : '#fef2f2', borderRadius: 8, padding: '12px 16px', fontSize: 13 }}>
              <strong style={{ color: isConfigured ? '#16a34a' : '#dc2626' }}>
                M2M Status: {isConfigured ? '✓ Connected' : '✗ Not configured'}
              </strong>
              {!isConfigured && <span style={{ color: '#64748b', marginLeft: 8 }}>Add an API key above to enable M2M integration.</span>}
            </div>
          </div>
        )}

        {/* ── Webhook Log Tab ── */}
        {activeTab === 'webhook' && (
          <div>
            <h2 style={{ margin: '0 0 16px', fontSize: 17, color: '#0f172a' }}>Webhook Event Log</h2>
            {webhookEvents.length === 0 ? (
              <div style={{ padding: '32px 0', textAlign: 'center', color: '#94a3b8' }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>📡</div>
                <p>No webhook events received yet.</p>
                <p style={{ fontSize: 13 }}>Configure your GHL webhook URL to start receiving events.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {webhookEvents.map((evt, i) => (
                  <div key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>{evt.type || 'Unknown event'}</span>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>{evt.receivedAt ? new Date(evt.receivedAt).toLocaleString() : ''}</span>
                    </div>
                    {evt.contactId && <div style={{ fontSize: 12, color: '#64748b' }}>Contact: {evt.contactId}</div>}
                    {evt.stage && <div style={{ fontSize: 12, color: '#64748b' }}>Stage: {evt.stage}</div>}
                    <details style={{ marginTop: 6 }}>
                      <summary style={{ fontSize: 12, color: '#3b82f6', cursor: 'pointer' }}>Raw payload</summary>
                      <pre style={{ fontSize: 11, background: '#1e293b', color: '#e2e8f0', padding: '8px 10px', borderRadius: 6, overflowX: 'auto', marginTop: 6 }}>
                        {JSON.stringify(evt.data, null, 2)}
                      </pre>
                    </details>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── System Health Tab ── */}
        {activeTab === 'health' && (
          <div>
            <h2 style={{ margin: '0 0 16px', fontSize: 17, color: '#0f172a' }}>System Health</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
              {[
                { label: 'Backend API', status: healthStatus?.status === 'ok' ? 'up' : 'down', detail: healthStatus?.model || 'checking...' },
                { label: 'GHL Integration', status: isConfigured ? 'up' : 'not_configured', detail: isConfigured ? 'API key set' : 'Not configured' },
                { label: 'Auth Service', status: 'up', detail: 'localStorage session' },
                { label: 'Deal Storage', status: 'up', detail: 'localStorage + state' },
              ].map(s => (
                <div key={s.label} style={{ background: s.status === 'up' ? '#f0fdf4' : s.status === 'not_configured' ? '#fffbeb' : '#fef2f2', border: `1px solid ${s.status === 'up' ? '#bbf7d0' : s.status === 'not_configured' ? '#fde68a' : '#fecaca'}`, borderRadius: 8, padding: '14px 16px' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{s.label}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.status === 'up' ? '#16a34a' : s.status === 'not_configured' ? '#d97706' : '#dc2626', display: 'inline-block' }} />
                    <span style={{ fontSize: 12, color: '#64748b' }}>{s.status === 'up' ? 'Online' : s.status === 'not_configured' ? 'Not configured' : 'Offline'}</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{s.detail}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 20, background: '#f8fafc', borderRadius: 8, padding: '14px 18px' }}>
              <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 8, fontSize: 14 }}>Environment</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', fontSize: 12 }}>
                <span style={{ color: '#64748b' }}>GHL API</span><span>{import.meta.env.VITE_GHL_API_KEY ? '✓ Set via .env' : '✗ Not set'}</span>
                <span style={{ color: '#64748b' }}>Backend URL</span><span>{import.meta.env.VITE_API_URL || window.location.origin}</span>
                <span style={{ color: '#64748b' }}>Build Mode</span><span>{import.meta.env.MODE}</span>
              </div>
            </div>
          </div>
        )}

        {/* ── Role Manager Tab ── */}
        {activeTab === 'roles' && (
          <div>
            <h2 style={{ margin: '0 0 16px', fontSize: 17, color: '#0f172a' }}>Role Manager</h2>
            <div style={{ background: '#f8fafc', borderRadius: 8, padding: '14px 18px', marginBottom: 20 }}>
              <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
                Current user: <strong style={{ color: '#0f172a' }}>{user?.name || user?.email}</strong>
                &nbsp;·&nbsp;Role: <strong style={{ color: '#0f172a' }}>{user?.role}</strong>
              </p>
            </div>
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 14, color: '#475569', marginBottom: 10 }}>Change Your Role (Testing)</h3>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {Object.values(ROLES).map(role => (
                  <button
                    key={role}
                    onClick={() => updateUserRole(role)}
                    style={{
                      padding: '7px 16px', borderRadius: 7, border: '1.5px solid',
                      borderColor: user?.role === role ? '#1B3A6B' : '#e2e8f0',
                      background: user?.role === role ? '#1B3A6B' : '#fff',
                      color: user?.role === role ? '#fff' : '#475569',
                      fontWeight: 600, cursor: 'pointer', fontSize: 13,
                    }}
                  >
                    {role}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h3 style={{ fontSize: 14, color: '#475569', marginBottom: 10 }}>Role Permissions Reference</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    {['Role', 'Billing', 'Admin', 'Dev Console', 'User Mgmt', 'Lender Mgmt', 'Checklist Upload', 'Deals'].map(h => (
                      <th key={h} style={{ background: '#f1f5f9', padding: '7px 10px', textAlign: 'left', border: '1px solid #e2e8f0', fontWeight: 600, color: '#475569' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { role: 'host',       billing: '✓', admin: '✓', dev: '✓', users: '✓', lenders: '✓', checklist: '✓', deals: '✓' },
                    { role: 'admin',      billing: '—', admin: '✓', dev: '—', users: '✓', lenders: '✓', checklist: '✓', deals: '✓' },
                    { role: 'developer',  billing: '—', admin: '—', dev: '✓', users: '—', lenders: '—', checklist: '—', deals: '—' },
                    { role: 'team_member',billing: '—', admin: '—', dev: '—', users: '—', lenders: '—', checklist: '—', deals: '✓' },
                  ].map(r => (
                    <tr key={r.role} style={{ background: user?.role === r.role ? '#eff6ff' : '' }}>
                      <td style={{ padding: '7px 10px', border: '1px solid #e2e8f0', fontWeight: 600 }}>{r.role}</td>
                      {[r.billing, r.admin, r.dev, r.users, r.lenders, r.checklist, r.deals].map((v, i) => (
                        <td key={i} style={{ padding: '7px 10px', border: '1px solid #e2e8f0', textAlign: 'center', color: v === '✓' ? '#16a34a' : '#94a3b8' }}>{v}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DevConsolePage
